import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../hooks/useAuth';
import { Package, Download, Plus, LogOut, User, Key, RefreshCw, Menu, FileText } from 'lucide-react';
import { Button, Dialog } from '../ui';
import PasswordChangeForm from '../PasswordChangeForm';
import { ThemeToggle } from '../ThemeToggle';

/**
 * Header Component
 *
 * Main application header with logo, title, user info, and action buttons.
 * Displays the Add Item, Excel export, and Logout buttons.
 * Uses React.memo for performance optimization.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onAddItem - Add item button click handler
 * @param {Function} props.onExport - Excel export button click handler
 * @returns {JSX.Element} Application header component
 */
const Header = React.memo(function Header({ onAddItem, onExport, onExportPDF }) {
  const { profile, signOut } = useAuth();
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle cache clear + reload
  const handleClearCache = useCallback(async () => {
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
      }
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload(true);
    } catch (e) {
      window.location.reload(true);
    }
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      // Error handled in useAuth hook
    }
  }, [signOut]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-0">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-pink-500 via-rose-500 to-pink-400" />
        
        <div className="container mx-auto px-3 py-2">
          {/* Desktop layout */}
          <div className="hidden md:flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-pink-500/30">
                <Package className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gradient">Deals Manager</h1>
                <p className="text-xs text-muted-foreground">
                  ნივთების მართვის სისტემა
                </p>
              </div>
            </div>

            {/* User Info and Actions */}
            <div className="flex items-center gap-3">
              {/* User Info */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
                <div className="text-sm">
                  <p className="font-medium text-foreground">
                    {profile?.full_name || 'მომხმარებელი'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {profile?.email || ''}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onExport} size="sm" className="rounded-xl">
                  <Download className="h-4 w-4 mr-1.5" />
                  Excel
                </Button>
                <Button variant="outline" onClick={onExportPDF} size="sm" className="rounded-xl">
                  <FileText className="h-4 w-4 mr-1.5" />
                  PDF
                </Button>
                <Button onClick={onAddItem} size="sm" className="rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 shadow-lg shadow-pink-500/30">
                  <Plus className="h-4 w-4 mr-1.5" />
                  ნივთის დამატება
                </Button>
              </div>

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* Change Password Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsPasswordDialogOpen(true)}
                aria-label="პაროლის შეცვლა"
                className="rounded-xl"
              >
                <Key className="h-4 w-4" />
              </Button>

              {/* Clear Cache Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClearCache}
                aria-label="ქეშის წაშლა"
                title="ქეშის წაშლა და განახლება"
                className="rounded-xl"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>

              {/* Logout Button */}
              <Button
                variant="destructive"
                size="icon"
                onClick={handleLogout}
                aria-label="გამოსვლა"
                title="გამოსვლა"
                className="rounded-xl"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Mobile layout */}
          <div className="flex md:hidden items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center shadow-md">
                <Package className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg text-gradient">DM</span>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Mobile add button */}
              <Button 
                onClick={onAddItem}
                size="sm"
                className="h-9 w-9 p-0 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 shadow-lg"
                aria-label="ნივთის დამატება"
              >
                <Plus className="h-5 w-5" />
              </Button>

              {/* User avatar */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center text-white font-medium text-sm shadow-md"
                aria-label="მენიუ"
              >
                {profile?.full_name?.charAt(0) || profile?.email?.charAt(0) || 'U'}
              </button>
            </div>
          </div>

          {/* Mobile expanded menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 animate-slide-down">
              <div className="flex flex-col gap-3">
                {/* User info */}
                <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{profile?.full_name || 'მომხმარებელი'}</p>
                    <p className="text-xs text-muted-foreground">{profile?.email || ''}</p>
                  </div>
                  <ThemeToggle />
                </div>

                {/* Actions */}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={onExport} size="sm" className="rounded-xl">
                    <Download className="h-4 w-4 mr-1" />
                    Excel
                  </Button>
                  <Button variant="outline" onClick={onExportPDF} size="sm" className="rounded-xl">
                    <FileText className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsPasswordDialogOpen(true)}
                    className="rounded-xl"
                  >
                    <Key className="h-4 w-4 mr-1" />
                    პაროლი
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearCache}
                    className="rounded-xl"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    განახლება
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleLogout}
                    className="rounded-xl"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    გამოსვლა
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Password Change Dialog */}
      <PasswordChangeForm
        isOpen={isPasswordDialogOpen}
        onClose={() => setIsPasswordDialogOpen(false)}
      />
    </>
  );
});

// PropTypes for type checking
Header.propTypes = {
  onAddItem: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onExportPDF: PropTypes.func.isRequired,
};

export default Header;